/**
 * Voice question screen for the onboarding flow.
 *
 * Types the question out one character at a time, then reveals the
 * record button. Manages record → stop → re-record → next. Swiping
 * anywhere advances only once an answer is recorded so the user
 * cannot skip past a question.
 *
 * Layout mirrors the intro screens: the question sits at the visual
 * center, the record UI floats just below it, the SwipeHint lives
 * at the bottom, and the re-record escape hatch sits above it so it
 * does not compete with the advance affordance.
 */
import { useRef, useState } from "react";
import { Mic, Play, RotateCcw, Square } from "lucide-react";

import { Button } from "@/ui/Button";
import { usePlatform } from "@/hooks/usePlatform";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import type { VoiceRecording } from "@/types/voice";

import type { EnrollmentAnswer } from "./types";
import { SwipeHint } from "./SwipeHint";
import { TypedText } from "./TypedText";
import { useSwipe } from "./useSwipe";

interface QuestionScreenProps {
  question: { id: string; text: string };
  questionIndex: number;
  totalQuestions: number;
  onSubmit: (answer: EnrollmentAnswer) => void;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function QuestionScreen({
  question,
  questionIndex,
  totalQuestions,
  onSubmit,
}: QuestionScreenProps) {
  const { haptic } = usePlatform();
  const {
    permission,
    requestPermission,
    isRecording,
    elapsedSeconds,
    start,
    stop,
    isPlaying,
    play,
    stopPlayback,
  } = useVoiceRecording();

  const [localRecording, setLocalRecording] = useState<VoiceRecording | null>(null);
  const [questionDone, setQuestionDone] = useState(false);
  const submittedRef = useRef(false);

  const handleRecord = async () => {
    haptic("medium");
    if (permission !== "granted") {
      const next = await requestPermission();
      if (next !== "granted") return;
    }
    setLocalRecording(null);
    await start();
  };

  const handleStop = async () => {
    haptic("light");
    const result = await stop();
    if (result) setLocalRecording(result);
  };

  const handlePlay = async () => {
    if (!localRecording) return;
    haptic("light");
    if (isPlaying) {
      await stopPlayback();
    } else {
      await play(localRecording.path);
    }
  };

  const handleReRecord = async () => {
    haptic("light");
    if (isPlaying) await stopPlayback();
    setLocalRecording(null);
    await start();
  };

  const advance = async () => {
    if (!localRecording || submittedRef.current) return;
    submittedRef.current = true;
    haptic("medium");
    if (isPlaying) await stopPlayback();
    onSubmit({
      questionId: question.id,
      question: question.text,
      audioPath: localRecording.path,
      durationSeconds: localRecording.durationSeconds,
    });
  };

  const swipe = useSwipe(() => {
    if (localRecording) advance();
  });

  return (
    <div className="absolute inset-0" {...swipe}>
      <div className="absolute left-0 right-0 top-[calc(env(safe-area-inset-top,0px)+1.5rem)] text-center">
        <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
          {questionIndex + 1} of {totalQuestions}
        </span>
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-12 px-8">
        <h2 className="text-balance text-center text-[28px] font-medium leading-snug">
          <TypedText
            key={question.id}
            text={question.text}
            onComplete={() => setQuestionDone(true)}
          />
        </h2>

        <div
          className={`flex flex-col items-center gap-5 transition-opacity duration-500 ${
            questionDone ? "opacity-100" : "opacity-0"
          }`}
        >
          {!isRecording && !localRecording && (
            <Button
              size="icon"
              className="h-20 w-20 rounded-full"
              aria-label="Record answer"
              onClick={handleRecord}
              disabled={permission === "denied" || !questionDone}
            >
              <Mic className="h-8 w-8" />
            </Button>
          )}

          {isRecording && (
            <>
              <Button
                size="icon"
                variant="destructive"
                className="h-20 w-20 animate-pulse rounded-full"
                aria-label="Stop recording"
                onClick={handleStop}
              >
                <Square className="h-8 w-8" fill="currentColor" />
              </Button>
              <span className="font-mono text-2xl tabular-nums text-muted-foreground">
                {formatDuration(elapsedSeconds)}
              </span>
            </>
          )}

          {!isRecording && localRecording && (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="h-14 w-14 rounded-full"
                aria-label={isPlaying ? "Stop playback" : "Play answer"}
                onClick={handlePlay}
              >
                {isPlaying ? (
                  <Square className="h-5 w-5" fill="currentColor" />
                ) : (
                  <Play className="h-5 w-5" fill="currentColor" />
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                {formatDuration(Math.round(localRecording.durationSeconds))}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReRecord}
                className="gap-1 text-xs uppercase tracking-[0.2em] text-muted-foreground"
              >
                <RotateCcw className="h-3 w-3" />
                Record again
              </Button>
            </>
          )}

          {permission === "denied" && (
            <p className="max-w-xs text-center text-sm text-destructive">
              Microphone access is off. Turn it on in Settings to continue.
            </p>
          )}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]">
        <SwipeHint ready={!!localRecording && !isRecording} onAdvance={advance} />
      </div>
    </div>
  );
}
